import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { NsKey } from 'enums/NsKey';
import { usePendingValidationsCount } from '../hooks/usePendingValidationsCount';

/** Polling period of the menu counter (the validator's in-app signal). */
const REFRESH_INTERVAL_MS = 60_000;

interface PendingValidationsMenuBadgeProps {
  /** Collapsed menu: bubble over the icon instead of a pill after the label. */
  isCollapsed: boolean;
}

/**
 * Counter of the pending validation requests on the « Validations » menu entry.
 * The entry is only listed for sessions holding the validations capability
 * (menuPolicy), so the endpoint is never called for other sessions. Refreshed
 * on mount (menu opening), on every navigation and every minute; hidden when
 * nothing is pending or the count cannot be loaded.
 */
export const PendingValidationsMenuBadge: React.FC<PendingValidationsMenuBadgeProps> = ({
  isCollapsed
}) => {
  const { t } = useTranslation(NsKey.Validation);
  const { pathname } = useLocation();
  const { count, refresh } = usePendingValidationsCount({
    refreshIntervalMs: REFRESH_INTERVAL_MS
  });

  // The hook already fetches on mount: only re-fetch on later navigations
  // (e.g. back to the list after answering a request).
  const isFirstPathRef = useRef(true);
  useEffect(() => {
    if (isFirstPathRef.current) {
      isFirstPathRef.current = false;
      return;
    }
    refresh();
  }, [pathname, refresh]);

  if (!count) return null;

  const label = t('VALIDATION.MENU.PENDING_BADGE', { count });

  return (
    <span
      title={label}
      aria-label={label}
      className={clsx(
        'flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white',
        isCollapsed && 'absolute -right-3 -top-3'
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
};

export default PendingValidationsMenuBadge;
